import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Brandmark, Wordmark } from "@/components/Brand";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type Mode = "signin" | "signup" | "magic";

const MAGIC_COOLDOWN_SECONDS = 30;

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { session, loading: authLoading } = useAuth();

  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [magicCooldown, setMagicCooldown] = useState(0);
  const [banner, setBanner] = useState<string | null>(null);

  // Priority: explicit `?next=` param (used by /invite) > router state.from > "/"
  const nextParam = params.get("next");
  const from =
    nextParam ??
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    "/";

  // Surface any error that came through /auth/callback (expired link, OAuth
  // denial, etc.) so the user knows why they're back on the sign-in page.
  useEffect(() => {
    const err = params.get("error");
    if (err) setBanner(err);
  }, [params]);

  useEffect(() => {
    if (!authLoading && session) navigate(from, { replace: true });
  }, [session, authLoading, navigate, from]);

  useEffect(() => {
    if (magicCooldown <= 0) return;
    const t = setTimeout(() => setMagicCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [magicCooldown]);

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
            data: { display_name: displayName, brand_name: brandName },
          },
        });
        if (error) throw error;
        toast({
          title: "Account created",
          description: "Check your inbox to verify your email.",
        });
        // If the project has email confirmation disabled, Supabase will
        // already have issued a session and the auth listener will route
        // us into the app. If confirmation is required, session is null —
        // send the user to the verify page.
        const { data: { session: s } } = await supabase.auth.getSession();
        navigate(s ? from : "/verify-email", { replace: true });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Authentication failed", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMagicSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (magicCooldown > 0) return;
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        // Don't auto-create accounts via magic link. Sign-up should be a
        // deliberate action that captures display_name + brand_name.
        shouldCreateUser: false,
      },
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Magic link failed", description: error.message, variant: "destructive" });
      return;
    }
    setMagicCooldown(MAGIC_COOLDOWN_SECONDS);
    toast({
      title: "Check your inbox",
      description: "We sent a sign-in link. It expires in 10 minutes.",
    });
  }

  async function handleOAuth(provider: "google" | "azure") {
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      toast({
        title: `${provider === "google" ? "Google" : "Microsoft"} sign-in failed`,
        description: error.message,
        variant: "destructive",
      });
      setSubmitting(false);
    }
    // On success, the browser is already redirecting — nothing more to do.
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto flex min-h-screen max-w-[1080px] flex-col items-center justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <Brandmark size={32} />
          <Wordmark className="text-[24px]" />
        </div>

        <div className="w-full max-w-[420px] rounded-xl border border-line bg-canvas p-8 shadow-[0_1px_2px_rgba(26,25,21,.04)]">
          <h1 className="font-serif text-[32px] leading-[1.1] text-ink">
            {mode === "signin" && "Welcome back"}
            {mode === "signup" && (
              <>Set up your <em className="italic text-accent-rf">workspace</em></>
            )}
            {mode === "magic" && (
              <>Sign in by <em className="italic text-accent-rf">email</em></>
            )}
          </h1>
          <p className="mt-2 text-[13px] text-ink-3">
            {mode === "signin" && "Sign in to manage your Shopify fulfillment with Claude."}
            {mode === "signup" && "Create an account to connect your store and start shipping smarter."}
            {mode === "magic" && "We'll send you a one-time link. No password needed."}
          </p>

          {banner && (
            <div
              role="alert"
              className="mt-5 rounded-md border border-berry/40 bg-berry/10 px-3 py-2 text-[12.5px] text-ink-2"
            >
              {banner}
            </div>
          )}

          {/* Mode tabs — only shown on signin / magic. Signup is reached via
              the "Create an account" link at the bottom to keep the
              primary-action choice simple. */}
          {mode !== "signup" && (
            <div role="tablist" aria-label="Sign-in method" className="mt-6 inline-flex rounded-md border border-line bg-canvas-2 p-0.5 text-[12px]">
              <TabButton active={mode === "signin"} onClick={() => setMode("signin")}>Password</TabButton>
              <TabButton active={mode === "magic"} onClick={() => setMode("magic")}>Magic link</TabButton>
            </div>
          )}

          {/* OAuth — always visible. Primary to keep the friction low. */}
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleOAuth("google")}
              disabled={submitting}
              className="flex items-center justify-center gap-2 rounded-md border border-line bg-canvas px-3 py-2.5 text-[12.5px] font-medium text-ink transition-colors duration-[120ms] hover:bg-canvas-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <GoogleIcon />
              Google
            </button>
            <button
              type="button"
              onClick={() => handleOAuth("azure")}
              disabled={submitting}
              className="flex items-center justify-center gap-2 rounded-md border border-line bg-canvas px-3 py-2.5 text-[12.5px] font-medium text-ink transition-colors duration-[120ms] hover:bg-canvas-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <MicrosoftIcon />
              Microsoft
            </button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-line" />
            <div className="text-[10px] uppercase tracking-[0.14em] text-ink-3">or</div>
            <div className="h-px flex-1 bg-line" />
          </div>

          {mode === "magic" ? (
            <form onSubmit={handleMagicSubmit} className="space-y-3">
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="varun@brand.in"
                  className="auth-input"
                  autoComplete="email"
                />
              </Field>
              <button
                type="submit"
                disabled={submitting || magicCooldown > 0}
                className="mt-2 w-full rounded-md bg-ink px-4 py-2.5 text-[13px] font-medium text-canvas transition-colors duration-[120ms] hover:bg-ink/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                {submitting
                  ? "Sending…"
                  : magicCooldown > 0
                  ? `Resend available in ${magicCooldown}s`
                  : "Send magic link"}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              {mode === "signup" && (
                <>
                  <Field label="Your name">
                    <input
                      type="text"
                      required
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Varun Bhardwaj"
                      className="auth-input"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Brand name">
                    <input
                      type="text"
                      required
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="AGEON"
                      className="auth-input"
                      autoComplete="organization"
                    />
                  </Field>
                </>
              )}
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="varun@brand.in"
                  className="auth-input"
                  autoComplete="email"
                />
              </Field>
              <Field
                label="Password"
                trailing={
                  mode === "signin" ? (
                    <Link
                      to="/forgot-password"
                      className="text-[11px] text-ink-3 underline-offset-2 hover:text-ink hover:underline"
                    >
                      Forgot?
                    </Link>
                  ) : null
                }
              >
                <input
                  type="password"
                  required
                  minLength={mode === "signup" ? 8 : 6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="auth-input"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 w-full rounded-md bg-ink px-4 py-2.5 text-[13px] font-medium text-canvas transition-colors duration-[120ms] hover:bg-ink/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                {submitting ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-[12px] text-ink-3">
            {mode === "signup" ? "Already have an account?" : "New to Routeflow?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
              className="font-medium text-ink underline-offset-2 hover:underline"
            >
              {mode === "signup" ? "Sign in" : "Create an account"}
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-ink-3">
          By continuing you agree to Routeflow's
          {" "}<Link to="/terms" className="underline-offset-2 hover:underline">terms</Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline-offset-2 hover:underline">privacy policy</Link>.
        </p>

        <div className="mt-6 rounded-md border border-line bg-canvas-2/50 px-4 py-3 text-center text-[12.5px] text-ink-2">
          Just want to look around?{" "}
          <Link
            to="/demo"
            className="font-medium text-accent underline-offset-2 hover:underline"
          >
            Open the demo
          </Link>{" "}
          — no signup, sample data, two minutes to explore.
        </div>
      </div>

      <style>{`
        .auth-input {
          width: 100%;
          border-radius: 6px;
          border: 1px solid hsl(var(--line));
          background: hsl(var(--canvas));
          padding: 9px 12px;
          font-size: 13px;
          color: hsl(var(--ink));
          transition: border-color 120ms;
        }
        .auth-input::placeholder { color: hsl(var(--ink-3)); }
        .auth-input:focus { outline: none; border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded-[5px] px-3 py-1.5 font-medium transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "bg-canvas text-ink shadow-[0_1px_1px_rgba(26,25,21,.06)]" : "text-ink-3 hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  children,
  trailing,
}: {
  label: string;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.1em] text-ink-3">
        <span>{label}</span>
        {trailing}
      </div>
      {children}
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.5 36.6 44 30.9 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
      <path fill="#f35325" d="M1 1h10v10H1z"/>
      <path fill="#81bc06" d="M12 1h10v10H12z"/>
      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
      <path fill="#ffba08" d="M12 12h10v10H12z"/>
    </svg>
  );
}
